
import httpBaseQuery from 'shared/api/rtk-query/httpBaseQuery';
import {buildQuery} from 'shared/api/query';
import {providesList} from 'shared/api/rtk-query/RTKQueryHelper';
import IOrderBy from 'shared/model/IOrderBy';
import {invalidateHttpMonitors} from 'entities/monitor';

import {INotifyChannel, INotifyChannelEdit, ITestNotifyResult} from '../model/INotifyChannel';

import type {EndpointBuilder} from '@reduxjs/toolkit/dist/query/endpointDefinitions';

export const NOTIFY_CHANNEL_TAGS = ['Item', 'ItemActive'] as const;

type NotifyChannelTag = typeof NOTIFY_CHANNEL_TAGS[number];

type HttpBaseQuery = ReturnType<typeof httpBaseQuery>;

export interface INotifyChannelUrls {
    list: (_sort?: IOrderBy, _params?: string[]) => string,
    item: (_id: number) => string,
    testNotify: (_id: number) => string,
}

export const notifyChannelApiOptions = {
    tagTypes: NOTIFY_CHANNEL_TAGS,
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
};

const LIST_TAGS = [
    {type: 'Item' as const, id: 'LIST'},
    {type: 'ItemActive' as const, id: 'LIST'},
];

export const notifyChannelEndpoints = <T extends INotifyChannel, TAdd extends object, TEdit extends INotifyChannelEdit>(
    urls: INotifyChannelUrls,
) => <R extends string>(build: EndpointBuilder<HttpBaseQuery, NotifyChannelTag, R>) => ({
        getList: build.query<T[], IOrderBy | undefined>({
            query: (sort) => ({
                path: buildQuery(urls.list(sort)),
            }),
            providesTags: (result) => providesList(result, 'Item'),
        }),
        getActive: build.query<T[], void>({
            query: () => ({
                path: buildQuery(urls.list(undefined, ['active=true'])),
            }),
            providesTags: (result) => providesList(result, 'ItemActive'),
        }),
        add: build.mutation<T, TAdd>({
            query: (data) => ({
                path: urls.list(),
                method: 'POST',
                data,
            }),
            invalidatesTags: LIST_TAGS,
        }),
        update: build.mutation<T, TEdit>({
            query: ({id, ...data}) => ({
                path: urls.item(id),
                method: 'PUT',
                data,
            }),
            invalidatesTags: LIST_TAGS,
        }),
        delete: build.mutation<void, number>({
            query: (id) => ({
                path: urls.item(id),
                method: 'DELETE',
            }),
            async onQueryStarted(id, {dispatch, queryFulfilled}) {
                const result = await queryFulfilled.catch(() => null);
                if (result) {
                    invalidateHttpMonitors(dispatch);
                }
            },
            invalidatesTags: (result, error, id) => [
                ...LIST_TAGS,
                {type: 'Item', id},
                {type: 'ItemActive', id},
            ],
        }),
        sendTestNotify: build.mutation<ITestNotifyResult | void, number>({
            query: (id) => ({
                path: urls.testNotify(id),
                method: 'POST',
                data: {},
            }),
        }),
    });
